import { toSeconds } from '../../lib/dateTime';
import { addScreenTimeReport } from '../../report/';
import { ScreenTime } from '../../types/store/screenTime';

// Max. active time that we will assume if the device was asleep
const activeIntervalInMillis = 60_000; // 1 minute

const maxSlotTimeInMillis = 30_000; // 30 seconds

/**
 * Returns if it should complete the current slot (active device).
 */
export const isSlotCompletedWhileScreenOn = (
  elapsedTimeSlotInMillis: number
) => {
  return (
    isSlotCompleted(elapsedTimeSlotInMillis) &&
    elapsedTimeSlotInMillis >= maxSlotTimeInMillis
  );
};

/**
 * Complete the current slot for an active device.
 */
export const onSlotCompletedWhileScreenOn = (
  screenTime: ScreenTime,
  elapsedTimeSlotInMillis: number,
  elapsedTimeSinceLastActiveInMillis: number,
  currentTime: number
) => {
  onSlotCompleted(
    screenTime,
    elapsedTimeSlotInMillis,
    elapsedTimeSinceLastActiveInMillis
  );

  screenTime.slotStartTimestamp = currentTime;

  return screenTime;
};

/**
 * Returns if it should complete the current slot (locked device).
 */
export const isSlotCompletedWhileScreenOff = (
  elapsedTimeSlotInMillis: number,
  slotStartTimestamp: number
) => {
  return isSlotCompleted(elapsedTimeSlotInMillis) && slotStartTimestamp !== -1;
};

/**
 * Complete the current slot for a locked device.
 */
export const onSlotCompletedWhileScreenOff = (
  screenTime: ScreenTime,
  elapsedTimeSlotInMillis: number,
  elapsedTimeSinceLastActiveInMillis: number
) => {
  onSlotCompleted(
    screenTime,
    elapsedTimeSlotInMillis,
    elapsedTimeSinceLastActiveInMillis
  );

  screenTime.slotStartTimestamp = -1;
  screenTime.lastActiveTimestamp = -1;

  return screenTime;
};

export const onSlotCompleted = (
  screenTime: ScreenTime,
  elapsedTimeSlotInMillis: number,
  elapsedTimeSinceLastActiveInMillis: number
) => {
  // Discard negative values that can occur during timezone transitions.
  // When the account timezone changes significantly (e.g., Melbourne → LA),
  // the "time until midnight" calculation can produce negative values.
  if (elapsedTimeSlotInMillis < 0) {
    screenTime.elapsedTimeSlotInMillis = 0;
    return screenTime;
  }

  // The time since the slot started should usually be from the time it was started until now.
  // But if the amount from last active is >activeIntervalInMillis, we assume that the device was asleep.
  elapsedTimeSlotInMillis =
    elapsedTimeSinceLastActiveInMillis > activeIntervalInMillis
      ? screenTime.elapsedTimeSlotInMillis + activeIntervalInMillis
      : elapsedTimeSlotInMillis;

  addScreenTimeReport(
    screenTime.slotStartTimestamp,
    toSeconds(elapsedTimeSlotInMillis)
  );

  // The incremented time should usually be from the time it was last active until now.
  // But if the las active time is >activeIntervalInMillis, we assume that the device was asleep and only increment activeIntervalInMillis.
  elapsedTimeSinceLastActiveInMillis =
    elapsedTimeSinceLastActiveInMillis > activeIntervalInMillis
      ? activeIntervalInMillis
      : elapsedTimeSinceLastActiveInMillis;

  screenTime.totalConsumedInMillis += elapsedTimeSinceLastActiveInMillis;
  screenTime.elapsedTimeSlotInMillis = 0;

  return screenTime;
};

/**
 * We should only count and report screen time for 1 second or bigger.
 * We should never count/report 0 seconds.
 */
const isSlotCompleted = (elapsedTimeSlotInMillis: number) => {
  return toSeconds(elapsedTimeSlotInMillis) > 0;
};
